/* eslint-disable */
import { useEffect, useState } from 'react';
import { Alert, Form, notification, Spin, Button } from 'antd';
import { RefetchOptions, QueryObserverResult, useMutation } from '@tanstack/react-query';
import { updateUser } from '@/api/user';
import { LoadingOutlined } from '@ant-design/icons';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { User } from '@/types/TAccounts';
import { LanguageGreet } from './GreetingPhraseInfo';
import TextArea from 'antd/es/input/TextArea';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { updateCampaignVariables } from '@/api/novi/CamapaignPortalApis';
import CopyableText from '@/components/LabelWithdescription/CopyableText';
import { getAllVariables } from '@/store/novi/variables/selector';
import { setVariable } from '@/store/novi/variables/slice';
import { Variable } from '@/types/TVariables';
import TextEditor from '@/components/TextEditor/TextEditor';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { safeJSONParse } from '@/utils/helper';

const validateGreetingPhrase = (value: string, business_name?: string) => {
	if (value && value?.trim()?.split(/\s+/)?.length > 15) {
		return Promise.reject(
			<Alert
				className="mt-2"
				type="warning"
				message={
					<>
						<b>Your greeting phrase is too long!</b>
						<p>
							When your greeting phrase is this long, 75% of people will hang up. We recommend you
							just stick with "Hello, welcome to{' '}
							<code className="bg-gray-100 px-2 py-1 rounded">
								{business_name ?? '{{business_name}}'}
							</code>
							". How may I help you?
						</p>
					</>
				}
				showIcon
			/>,
		);
	}
	return Promise.resolve();
};

export const GreetingPhraseForm: React.FC<{
	system_phrase?: string;
	languageGreets: LanguageGreet[];
	refetch: ((options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>) | (() => void);
	isNoviUser?: boolean;
}> = ({ system_phrase, refetch, languageGreets, isNoviUser }) => {
	const dispatch = useAppDispatch();
	const [form] = Form.useForm<{ system_phrase: string }>();
	const [formTouched, setFormTouched] = useState(false);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	const variables = useAppSelector(getAllVariables);

	let selectedCampaign: any;

	if (isNoviUser) {
		selectedCampaign = useAppSelector(getSelectedAgent);
	}

	useEffect(() => {
		form.validateFields();
	}, []);

	useEffect(() => {
		if (selectedCampaign?.variables?.length) {
			try {
				const parsedVariables = safeJSONParse(selectedCampaign.variables, {});
				Object.entries(parsedVariables).forEach(([key, value]) => {
					dispatch(setVariable({ name: key, data: value as Variable }));
				});
			} catch (error) {
				console.error('Error parsing variables:', error);
			}
		}
	}, [selectedCampaign, dispatch]);

	const mutation = useMutation({
		mutationKey: ['update-client'],
		mutationFn: updateUser,
		onSuccess: () => {
			notification.success({
				key: 'form-success',
				message: 'Greeting Phrase has been saved successfully!',
				duration: 3,
			});
			setFormTouched(false);
			refetch();
		},
	});

	const handleSubmit = (value: any) => {
		const languageGreetsObject = value?.languageGreets
			? value.languageGreets.reduce(
					(acc: any, item: any) => {
						acc[item.language] = item.greeting;
						return acc;
					},
					{} as Record<string, string>,
				)
			: {};

		mutation.mutate({
			system_phrase: value.system_phrase,
			language_greetings: JSON.stringify(languageGreetsObject),
			agentId: selectedAgentId as string,
		});
	};

	const { mutate: updateVariables } = useMutation({
		mutationKey: ['update-variables'],
		mutationFn: () => updateCampaignVariables(selectedCampaign?.number, variables, 'Client'),
		onSuccess: () => {
			notification.success({
				key: 'form-success',
				message: 'Variables have been saved successfully!',
				duration: 3,
			});
			setFormTouched(false);
			refetch();
		},
	});

	const handleVariableChange = (updatedVariables: any) => {
		Object.entries(updatedVariables).forEach(([key, value]) => {
			dispatch(setVariable({ name: key, data: value as Variable }));
		});

		updateVariables();
	};

	const handleValuesChange = () => {
		setFormTouched(true);
		form.validateFields();
	};

	return (
		<>
			<Spin
				spinning={mutation.isPending}
				indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
			>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						system_phrase,
						languageGreets,
					}}
					onFinish={handleSubmit}
					onValuesChange={handleValuesChange}
				>
					<Form.Item
						label={
							<div className="flex items-center gap-1">
								<span className="font-medium">English</span>
								<CopyableText text={system_phrase || ''} hideText />
							</div>
						}
						rules={[
							{
								warningOnly: true,
								validator: (_, value) => validateGreetingPhrase(value, loggedInUser?.businessName),
							},
						]}
						preserve={true}
						name="system_phrase"
						className="!mb-4"
					>
						{isNoviUser ? (
							<TextEditor
								placeholder="Please enter your greeting phrase"
								variables={variables}
								onChange={(_, variables) => {
									if (variables) {
										handleVariableChange(variables);
									}
								}}
							/>
						) : (
							<TextArea
								autoSize={{ minRows: 3, maxRows: 12 }}
								placeholder="Please enter your greeting phrase"
								onBlur={() => form.validateFields(['system_phrase'])}
							/>
						)}
					</Form.Item>

					<Form.List name="languageGreets">
						{(fields) => (
							<>
								{fields.map(({ key, name, ...restField }) => (
									<Form.Item
										key={key}
										label={
											<div className="flex items-center gap-1">
												<span className="font-medium">
													{' '}
													{languageGreets[name]?.language || 'Unknown'}
												</span>
												<CopyableText text={languageGreets[name]?.greeting || ''} hideText />
											</div>
										}
										rules={[
											{
												warningOnly: true,
												validator: (_, value) =>
													validateGreetingPhrase(value, loggedInUser?.businessName),
											},
										]}
										preserve={true}
										name={[name, 'greeting']}
										className="!mb-4"
									>
										<TextArea
											{...restField}
											autoSize={{ minRows: 3, maxRows: 12 }}
											placeholder="Please enter your greeting phrase for the selected language"
											onBlur={() => form.validateFields([['languageGreets', name, 'greeting']])}
										/>
									</Form.Item>
								))}
							</>
						)}
					</Form.List>

					<Alert
						className="mb-6"
						type="info"
						closable={false}
						message={
							<>
								<b>Please Note:</b>
								<p className="text-justify">
									{isNoviUser ? (
										<>
											Keep your greeting short and engaging, like "Welcome to{' '}
											<code className="bg-gray-100 px-2 py-1 rounded">{'{{business_name}}'}</code>,
											how may I help you?". Use dynamic variables like{' '}
											<code className="bg-gray-100 px-2 py-1 rounded">{'{{customer_name}}'}</code>{' '}
											to personalize the message. You can add or update variables in the{' '}
											<strong>Variables</strong> tab.
										</>
									) : (
										<>
											Your greeting phrase should be short and concise, like "Welcome to
											[business_name], how may I help you?". If it is too long, people will think
											the AI is just a voicemail and hang up.
										</>
									)}
								</p>
							</>
						}
					/>

					<div className="flex justify-between mt-6">
						<Button
							onClick={() => {
								form.resetFields();
								setFormTouched(false);
								refetch();
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							htmlType="submit"
							disabled={!formTouched}
							className="bg-blue-500 hover:bg-blue-600"
						>
							Update
						</Button>
					</div>
				</Form>
			</Spin>
		</>
	);
};
